import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, signedPdfUrl, API } from "@/lib/api";
import { BrandMark } from "@/components/TopNav";
import DocumentCanvas from "@/components/DocumentCanvas";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SealCheck, ShieldCheck, PenNib, Download, Prohibit, Spinner, EnvelopeSimple, Clock, XCircle, Stack, UserPlus } from "@phosphor-icons/react";

export default function Sign() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [adopted, setAdopted] = useState({});
  const [padTarget, setPadTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    api.get(`/sign/${token}`)
      .then((r) => { setSession(r.data); setConsented(!!r.data.recipient?.consented); })
      .catch((e) => setError(e.response?.data?.detail || "Signing link not found"));
  }, [token]);

  if (error) return <CenterCard icon={Prohibit} tint="#E63946" title="Link unavailable" subtitle={error} />;
  if (!session) return <div className="min-h-screen signer-dark flex items-center justify-center text-slate-400"><Spinner size={28} className="animate-spin" /></div>;
  if (session.status === "voided") return <CenterCard icon={Prohibit} tint="#E63946" title="Document voided" subtitle="This document was voided by the sender and can no longer be signed." />;

  if (declined || session.status === "declined" || session.recipient.status === "declined") {
    const reason = session.recipient.decline_reason || declineReason;
    return <CenterCard icon={XCircle} tint="#E63946" title="You declined to sign"
      subtitle={reason ? `Reason: “${reason}”. The sender has been notified.` : "The sender has been notified. No further action is needed."} />;
  }

  const alreadyDone = session.recipient.status === "signed" || result || session.status === "completed";
  if (alreadyDone) {
    const completed = result?.completed ?? session.status === "completed";
    return (
      <div className="min-h-screen signer-dark flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-10 text-center paper-shadow animate-fade-up">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-success flex items-center justify-center"><SealCheck size={34} weight="fill" color="#fff" /></div>
          <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-navy">You're all done</h1>
          <p className="mt-3 text-slate-500 leading-relaxed">
            Thank you, {session.recipient.name}. Your signature has been recorded and a legally-binding audit certificate was generated. {completed ? "All parties have signed. A copy of the completed document was emailed to you." : "You will receive an email with the completed document once all parties have signed."}
          </p>
          {completed && (
            <a href={signedPdfUrl(session.envelope_id)} className="block mt-6" data-testid="signer-download-btn">
              <Button className="w-full rounded-full bg-navy hover:bg-navy/90 gap-2 h-11"><Download size={16} /> Download Signed Copy</Button>
              {session.include_certificate !== false && (
                <span className="mt-2 block text-xs text-slate-400" data-testid="signer-download-cert-note">Includes the audit certificate</span>
              )}
            </a>
          )}
          <div className="mt-6 pt-6 border-t border-slate-100 text-left">
            <p className="text-sm font-semibold text-navy">Manage your agreements in one place</p>
            <p className="mt-1 text-sm text-slate-500 leading-relaxed">Create a free CaminoSign account to send and track your own documents for signature.</p>
            <a href={`/login?email=${encodeURIComponent(session.recipient.email)}`} className="block mt-4" data-testid="create-account-cta">
              <Button variant="outline" className="w-full rounded-full gap-2 h-11 border-navy/30 text-navy"><UserPlus size={16} weight="bold" /> Create a free account</Button>
            </a>
          </div>
          <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={14} weight="duotone" /> Secured by CaminoSign</div>
        </div>
      </div>
    );
  }

  if (session.recipient.role === "copy") {
    return <CenterCard icon={EnvelopeSimple} tint="#1A2B4C" title="You're a copy recipient"
      subtitle={`You'll receive the completed "${session.title}" once everyone has signed.`} />;
  }
  if (!session.is_turn) {
    return <CenterCard icon={Clock} tint="#F4A261" title="Waiting your turn"
      subtitle="This document is being signed in order. You'll be able to sign once the signers ahead of you have finished." />;
  }

  if (!consented) {
    return <ConsentGate token={token} name={session.recipient.name} onAccepted={() => setConsented(true)} />;
  }

  const requiredFields = session.fields.filter((f) => f.required && !f.readonly);
  const filledCount = requiredFields.filter((f) => values[f.id]).length;
  const allFilled = filledCount === requiredFields.length;

  const reusableFields = session.fields.filter((f) => (f.type === "signature" || f.type === "initials") && !f.readonly);
  const reusableRemaining = reusableFields.filter((f) => adopted[f.type] && !values[f.id]);

  const onSignatureClick = (field) => {
    const t = field.type;
    if (adopted[t]) {
      setValues((v) => ({ ...v, [field.id]: adopted[t] }));
      return;
    }
    setPadTarget(field);
  };

  const applyToAll = () => {
    setValues((v) => {
      const nv = { ...v };
      reusableFields.forEach((f) => {
        if (adopted[f.type] && !nv[f.id]) nv[f.id] = adopted[f.type];
      });
      return nv;
    });
    toast.success(`Applied to ${reusableRemaining.length} field${reusableRemaining.length === 1 ? "" : "s"}`);
  };

  const onAdopt = (dataUrl) => {
    if (!padTarget) return;
    setAdopted((a) => ({ ...a, [padTarget.type]: dataUrl }));
    setValues((v) => ({ ...v, [padTarget.id]: dataUrl }));
    setPadTarget(null);
  };

  const finish = async () => {
    if (!allFilled) {
      toast.error("Please complete all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/sign/${token}/complete`, {
        values: Object.entries(values).map(([field_id, value]) => ({ field_id, value })),
      });
      setResult(r.data);
      toast.success("Document signed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
      setSubmitting(false);
    }
  };

  const submitDecline = async () => {
    setDeclining(true);
    try {
      await api.post(`/sign/${token}/decline`, { reason: declineReason.trim() });
      setDeclineOpen(false);
      setDeclined(true);
      toast.success("You declined this document");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not decline");
    } finally {
      setDeclining(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 glass-dark border-b">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="shrink-0"><BrandMark light /></div>
          <div className="flex items-center gap-2 text-sm min-w-0">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: session.recipient.color }} />
            <span className="text-[#C2CAD0] hidden sm:inline shrink-0">Signing as</span>
            <span className="font-semibold text-white truncate">{session.recipient.name}</span>
          </div>
        </div>
      </header>

      <div className="bg-[#F7F3EF] border-b border-[#e6e2dd]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-[#292F34]">
          <PenNib size={18} weight="duotone" className="text-[#292F34] shrink-0" />
          <span className="font-semibold truncate">{session.title}</span>
          <span className="text-[#3a4045] hidden md:inline shrink-0">— please review and complete the highlighted fields.</span>
          {reusableRemaining.length > 0 && (
            <button
              onClick={applyToAll}
              data-testid="apply-to-all-btn"
              className="ml-auto shrink-0 flex items-center gap-1.5 rounded-full bg-[#292F34] px-3 py-1.5 text-xs font-semibold text-[#F7F3EF] hover:bg-[#3a4045] transition-colors"
            >
              <Stack size={14} weight="bold" /> <span className="hidden sm:inline">Apply to all {reusableRemaining.length} field{reusableRemaining.length === 1 ? "" : "s"}</span><span className="sm:hidden">Apply all ({reusableRemaining.length})</span>
            </button>
          )}
        </div>
      </div>

      <main className="flex-1 signer-dark overflow-y-auto thin-scroll p-6 md:p-10 pb-28">
        <div className="mx-auto" style={{ maxWidth: 760 }}>
          {session.message && (
            <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">{session.message}</div>
          )}
          <DocumentCanvas
            docId={session.document.id} pages={session.document.pages} fields={session.fields}
            recipients={[session.recipient]} mode="signer" values={values}
            onValueChange={(id, val) => setValues((v) => ({ ...v, [id]: val }))}
            onSignatureClick={onSignatureClick} maxWidth={760}
          />
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 z-40 glass-dark border-t">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 h-18 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="h-2 w-20 sm:w-40 rounded-full bg-white/10 overflow-hidden shrink-0">
              <div className="h-full rounded-full bg-[#F7F3EF] transition-all duration-300" style={{ width: `${requiredFields.length ? (filledCount / requiredFields.length) * 100 : 100}%` }} />
            </div>
            <span className="text-xs text-[#C2CAD0] shrink-0" data-testid="fields-progress">{filledCount}/{requiredFields.length} required</span>
          </div>
          <Button onClick={finish} disabled={!allFilled || submitting} data-testid="finish-signing-btn"
            className="rounded-full bg-success hover:bg-success/90 gap-2 h-11 px-5 sm:px-6 disabled:opacity-50 shrink-0">
            {submitting ? <><Spinner size={18} className="animate-spin" /> Finishing…</> : <><SealCheck size={18} weight="bold" /> Finish</>}
          </Button>
        </div>
      </footer>

      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40">
        <button onClick={() => setDeclineOpen(true)} data-testid="decline-open-btn"
          className="text-xs font-semibold text-[#C2CAD0] hover:text-[#F7F3EF] transition-colors underline underline-offset-2">
          Decline to sign
        </button>
      </div>

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
              <XCircle size={22} weight="duotone" className="text-destructive" /> Decline to sign
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">This stops the document and notifies the sender. Let them know why (optional).</p>
          <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason for declining (optional)" className="mt-2 min-h-[100px]" data-testid="decline-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button onClick={submitDecline} disabled={declining} data-testid="confirm-decline-btn"
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white gap-2">
              {declining ? <Spinner size={16} className="animate-spin" /> : <XCircle size={16} weight="bold" />} Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignaturePad
        open={!!padTarget}
        onClose={() => setPadTarget(null)}
        onAdopt={onAdopt}
        label={padTarget?.type === "initials" ? "Initials" : "Signature"}
        defaultName={padTarget?.type === "initials" ? initialsOf(session.recipient.name) : session.recipient.name}
      />
    </div>
  );
}

const initialsOf = (name) => (name || "").split(/\s+/).map((w) => w[0] || "").join("").toUpperCase().slice(0, 4);

function CenterCard({ icon: Icon, tint, title, subtitle }) {
  return (
    <div className="min-h-screen signer-dark flex items-center justify-center px-6">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-10 text-center paper-shadow animate-fade-up">
        <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: tint }}>
          <Icon size={32} weight="fill" color="#fff" />
        </div>
        <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-navy">{title}</h1>
        <p className="mt-3 text-slate-500 leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}


function ConsentGate({ token, name, onAccepted }) {
  const [agreed, setAgreed] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    if (!agreed) return;
    setSaving(true);
    try {
      await api.post(`/sign/${token}/consent`);
      onAccepted();
    } catch (e) {
      toast.error("Couldn't record your consent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen signer-dark flex flex-col items-center justify-center px-4 sm:px-6 py-12">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 sm:p-9 paper-shadow animate-fade-up" data-testid="consent-gate">
        <img src={`${API}/assets/logo`} alt="CaminoSign" style={{ height: 30, width: "auto" }} />
        <p className="mt-7 text-lg text-navy">
          Please read the{" "}
          <button type="button" onClick={() => setOpen(true)} data-testid="disclosure-link"
            className="font-semibold text-action underline underline-offset-2 hover:opacity-80">
            Electronic Record and Signature Disclosure
          </button>.
        </p>

        <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="consent-checkbox-label">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
            data-testid="consent-checkbox"
            className="mt-0.5 h-5 w-5 rounded border-slate-300 text-action focus:ring-action cursor-pointer shrink-0" />
          <span className="text-navy">
            I agree to use electronic records and signatures. <span className="text-destructive">*</span>
          </span>
        </label>

        <div className="mt-8 flex items-center justify-end gap-3">
          <Button onClick={accept} disabled={!agreed || saving} data-testid="consent-continue-btn"
            className="rounded-full bg-success hover:bg-success/90 h-11 px-8 gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? <><Spinner size={16} className="animate-spin" /> Please wait…</> : "Continue"}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-1.5 text-xs text-white/50">
        <ShieldCheck size={14} weight="duotone" /> Secured by CaminoSign
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="disclosure-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Electronic Record and Signature Disclosure</DialogTitle>
          </DialogHeader>
          <DisclosureText />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DisclosureText() {
  return (
    <div className="space-y-4 text-sm text-slate-600 leading-relaxed" data-testid="disclosure-text">
      <p>
        From time to time, CaminoSign (on behalf of the sending party, "we", "us" or "Company") may be required
        by law to provide to you certain written notices or disclosures. Described below are the terms and
        conditions for providing to you such notices and disclosures electronically through the CaminoSign system.
        Please read the information below carefully and thoroughly, and if you can access this information
        electronically to your satisfaction and agree to these terms and conditions, please confirm your agreement
        by selecting the check-box next to "I agree to use electronic records and signatures".
      </p>
      <div>
        <h3 className="font-semibold text-navy">Getting paper copies</h3>
        <p>
          At any time, you may request from us a paper copy of any record provided or made available electronically
          to you by us. You may request delivery of paper copies of such records by contacting us at{" "}
          <a href="mailto:documents@caminoproperties.com" className="text-action underline">documents@caminoproperties.com</a>.
          We may charge you a reasonable fee to cover the costs of providing paper copies, though we will not charge
          you for downloading or printing the documents yourself from CaminoSign.
        </p>
      </div>
      <div>
        <h3 className="font-semibold text-navy">Withdrawing your consent</h3>
        <p>
          You may withdraw your consent to receive records and signatures electronically at any time before you
          complete signing by declining to sign, or afterwards by contacting us at{" "}
          <a href="mailto:documents@caminoproperties.com" className="text-action underline">documents@caminoproperties.com</a>.
          If you withdraw consent, we may not be able to complete the transaction electronically.
        </p>
      </div>
      <div>
        <h3 className="font-semibold text-navy">Hardware and software requirements</h3>
        <p>
          To access and retain the electronic records, you will need a current web browser that supports 128-bit
          encryption, a valid email address, and a device capable of viewing and downloading PDF documents. You
          should be able to print or save the documents you receive for your records.
        </p>
      </div>
      <div>
        <h3 className="font-semibold text-navy">Consent to electronic records and signatures</h3>
        <p>
          By selecting "I agree to use electronic records and signatures", you confirm that you can access and read
          this disclosure, that you consent to conduct business electronically, and that your electronic signature on
          documents is the legal equivalent of your handwritten signature and is binding. A copy of the completed,
          signed document and an audit certificate will be emailed to you once signing is finished.
        </p>
      </div>
      <p className="text-xs text-slate-400">
        Questions? Contact us at{" "}
        <a href="mailto:documents@caminoproperties.com" className="text-action underline">documents@caminoproperties.com</a>.
      </p>
    </div>
  );
}
